import type { SortCriteria } from './search';
import { sortsToNames, sortsToShortNames } from './search';
// This can be any event we have in the exchange back-end
// Not listing all here, just the ones we need so far so we have more control on what gets plotted.
export type PortfolioChangeReason = 'deposit_applied' | 'withdrawal_requested' | 'position_settled';

export interface NonwithdrawableCredit {
    amount: number; // cents
    credit_id: string;
    earned_at: number; // unix seconds
    expires_at?: number; // unix seconds; omitted when the credit never expires
    // 'unlocked' = fully traded through; amount is the full awarded amount the user keeps
    status: 'active' | 'unlocked' | 'expired';
}

/** Sum of active credit amounts in cents; falls back to nonwithdrawable_amount when present. */
export const getNonwithdrawableCents = (portfolio: HistoricalPortfolioVo): number => {
    if (portfolio.nonwithdrawable_amount != null) return portfolio.nonwithdrawable_amount;
    if (portfolio.nonwithdrawable_credits) {
        return portfolio.nonwithdrawable_credits
            .filter((c) => c.status === 'active')
            .reduce((sum, c) => sum + c.amount, 0);
    }
    return 0;
};

// This model is the portfolio history data in the generic structure as sent by the backend
export interface HistoricalPortfolioVo {
    // Current timestamp for this measurement (IN SECONDS NOT MILLIS)
    ts: number;
    // Available balance in cents (money in cash)
    a: number;
    // Total portfolio value in cents (money in markets of any status)
    v: number;
    // Portfolio pending value in cents (money in markets on pending positions)
    pending_v: number;
    // Cumulative deposits up to this point in time
    cumulative_deposits: number;
    // Cumulative withdrawals up to this point in time
    cumulative_withdrawals: number;
    // Lifetime trading P&L in cents: (a + v) - cumulative_deposits + cumulative_withdrawals, computed server-side.
    // Optional during backend rollout — treat missing as "unknown," not 0.
    lifetime_pnl?: number;
    // Total unrealized PnL across all open positions, in cents
    unrealized_pnl?: number;
    // Non-withdrawable promotional credit balance in cents (omitted when zero)
    nonwithdrawable_amount?: number;
    // Per-credit breakdown of the non-withdrawable balance (omitted until backend ships)
    nonwithdrawable_credits?: NonwithdrawableCredit[];
}

// Don't change these values since they are stored in the local storage, only add new ones at the end
export enum PortfolioColumn {
    CONTRACTS = 'Contracts',
    UNREALIZED_RETURN = 'Unrealized return',
    REALIZED_RETURN = 'Realized return',
    LAST_TRADED = 'Last traded',
    TOTAL_RETURN = 'Total return',
    AVG = 'Avg',
    EXPOSURE = 'Exposure',
    CLOSING_IN = 'Closing in',
    CLOSING_ON = 'Closing on',
    MARKET_VALUE = 'Market value',
    BUY_SELL = 'Buy / Sell',
    // BUY_PRICE = 'Buy price',
    // SELL_PRICE = 'Sell price',
    VOLUME = 'Volume',
    OPEN_INT = 'Open int.',
    PAYOUT_IF_WIN = 'Payout if win',
    FEES = 'Fees paid',
    ALLOCATION = 'Allocation',
    ORDER_HISTORY = 'Order history',
}

export const PortfolioColumns = Object.values(PortfolioColumn);

interface PortfolioColumnProps {
    titlePortfolioPage: string;
    titleEditModal: string;
    sortCriteria?: SortCriteria;
    tip?: string;
    alignCenter?: boolean;
    alignLeft?: boolean;
    borderRight?: boolean;
    borderLeft?: boolean;
}

export const portfolioColumnProps: Record<PortfolioColumn, PortfolioColumnProps> = {
    [PortfolioColumn.CONTRACTS]: {
        titlePortfolioPage: sortsToShortNames['portfolioContracts'],
        titleEditModal: sortsToNames['portfolioContracts'],
        sortCriteria: 'portfolioContracts',
    },
    [PortfolioColumn.UNREALIZED_RETURN]: {
        titlePortfolioPage: sortsToShortNames['portfolioUnrealized'],
        titleEditModal: sortsToNames['portfolioUnrealized'],
        sortCriteria: 'portfolioUnrealized',
    },
    [PortfolioColumn.REALIZED_RETURN]: {
        titlePortfolioPage: sortsToShortNames['portfolioRealized'],
        titleEditModal: sortsToNames['portfolioRealized'],
        sortCriteria: 'portfolioRealized',
    },
    [PortfolioColumn.LAST_TRADED]: {
        titlePortfolioPage: sortsToShortNames['portfolioLtp'],
        titleEditModal: sortsToNames['portfolioLtp'],
        sortCriteria: 'portfolioLtp',
    },
    [PortfolioColumn.TOTAL_RETURN]: {
        titlePortfolioPage: sortsToShortNames['portfolioTotalReturn'],
        titleEditModal: sortsToNames['portfolioTotalReturn'],
        sortCriteria: 'portfolioTotalReturn',
        tip: `Total return includes both your most recent position and any past contracts you've bought or sold here.`,
    },
    [PortfolioColumn.AVG]: {
        titlePortfolioPage: sortsToShortNames['portfolioAvg'],
        titleEditModal: sortsToNames['portfolioAvg'],
        sortCriteria: 'portfolioAvg',
    },
    [PortfolioColumn.EXPOSURE]: {
        titlePortfolioPage: sortsToShortNames['portfolioExposure'],
        titleEditModal: sortsToNames['portfolioExposure'],
        sortCriteria: 'portfolioExposure',
        tip: `The costs displayed includes fees paid.`,
    },
    [PortfolioColumn.CLOSING_IN]: {
        titlePortfolioPage: 'DTE',
        titleEditModal: 'DTE',
        sortCriteria: 'portfolioClosing',
    },
    [PortfolioColumn.CLOSING_ON]: {
        titlePortfolioPage: 'Expiration date',
        titleEditModal: 'Expiration date',
        sortCriteria: 'portfolioClosing',
    },
    [PortfolioColumn.MARKET_VALUE]: {
        titlePortfolioPage: sortsToShortNames['portfolioMarketValue'],
        titleEditModal: sortsToNames['portfolioMarketValue'],
        sortCriteria: 'portfolioMarketValue',
    },
    [PortfolioColumn.BUY_SELL]: {
        titlePortfolioPage: sortsToShortNames['portfolioBuy'],
        titleEditModal: sortsToNames['portfolioBuy'],
        sortCriteria: 'portfolioBuy',
        alignCenter: true,
    },
    // [PortfolioColumn.BUY_PRICE]: {
    //     titlePortfolioPage: sortsToShortNames['portfolioBuy'],
    //     titleEditModal: sortsToNames['portfolioBuy'],
    //     sortCriteria: 'portfolioBuy',
    // },
    // [PortfolioColumn.SELL_PRICE]: {
    //     titlePortfolioPage: sortsToShortNames['portfolioSell'],
    //     titleEditModal: sortsToNames['portfolioSell'],
    //     sortCriteria: 'portfolioSell',
    // },
    [PortfolioColumn.VOLUME]: {
        titlePortfolioPage: sortsToShortNames['portfolioVolume'],
        titleEditModal: sortsToNames['portfolioVolume'],
        sortCriteria: 'portfolioVolume',
    },
    [PortfolioColumn.OPEN_INT]: {
        titlePortfolioPage: sortsToShortNames['portfolioInterest'],
        titleEditModal: sortsToNames['portfolioInterest'],
        sortCriteria: 'portfolioInterest',
    },
    [PortfolioColumn.PAYOUT_IF_WIN]: {
        titlePortfolioPage: sortsToShortNames['portfolioPayoutIfWin'],
        titleEditModal: sortsToNames['portfolioPayoutIfWin'],
        sortCriteria: 'portfolioPayoutIfWin',
    },
    [PortfolioColumn.FEES]: {
        titlePortfolioPage: sortsToShortNames['portfolioFees'],
        titleEditModal: sortsToNames['portfolioFees'],
        sortCriteria: 'portfolioFees',
    },
    [PortfolioColumn.ALLOCATION]: {
        titlePortfolioPage: sortsToShortNames['portfolioAllocation'],
        titleEditModal: sortsToNames['portfolioAllocation'],
        sortCriteria: 'portfolioAllocation',
    },
    [PortfolioColumn.ORDER_HISTORY]: {
        titlePortfolioPage: '',
        titleEditModal: 'Order history',
    },
};

// Don't change these values since they are stored in the local storage, only add new ones at the end
export enum HistoryPortfolioColumn {
    OUTCOME = 'Outcome',
    FINAL_POSITION = 'Final position',
    SETTLEMENT_PAYOUT = 'Settlement payout',
    COST = 'Cost',
    FEES = 'Fees',
    TOTAL_PAYOUT = 'Total payout',
    TOTAL_RETURN = 'Total return',
}

export const historyPortfolioColumnProps: Record<HistoryPortfolioColumn, PortfolioColumnProps> = {
    [HistoryPortfolioColumn.COST]: {
        titlePortfolioPage: 'Total cost',
        titleEditModal: 'Total cost',
        sortCriteria: 'portfolioExposure',
        tip: `The costs displayed includes fees paid.`,
    },
    [HistoryPortfolioColumn.FEES]: {
        titlePortfolioPage: 'Fees',
        titleEditModal: 'Fees',
        sortCriteria: 'portfolioFees',
    },
    [HistoryPortfolioColumn.FINAL_POSITION]: {
        titlePortfolioPage: 'Final position',
        titleEditModal: 'Final position',
        sortCriteria: 'portfolioContracts',
    },
    [HistoryPortfolioColumn.OUTCOME]: {
        titlePortfolioPage: 'Outcome',
        titleEditModal: 'Outcome',
        // borderRight: true,
    },
    [HistoryPortfolioColumn.SETTLEMENT_PAYOUT]: {
        titlePortfolioPage: 'Settlement payout',
        titleEditModal: 'Settlement payout',
        borderRight: true,
    },
    [HistoryPortfolioColumn.TOTAL_PAYOUT]: {
        titlePortfolioPage: 'Total payout',
        titleEditModal: 'Total payout',
    },
    [HistoryPortfolioColumn.TOTAL_RETURN]: {
        titlePortfolioPage: 'Total return',
        titleEditModal: 'Total return',
        sortCriteria: 'portfolioTotalReturn',
        tip: `Total return includes both your most recent position and any past contracts you've bought or sold here.`,
    },
};

export const HistoryPortfolioColumns = Object.values(HistoryPortfolioColumn);
